import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { BaseApiService } from '@api/services/base-api.service';
import { ApiServiceName } from '@api/enums/controllers';
import { ApiService } from '@api/services/api.service';
import { ManufactoryControllerEnum } from '@api/enums/controllers/manufactory-controller.enum';
import {
  DailyPlanCreateRequestForChange,
  DailyPlanDetailModel,
  DailyPlanListItemModel,
} from '@features/manufactory/models/daily-plans.model';
import { DataListModel } from '@core/api/models/shared';
import { DataListOffsetPageInfoModel } from '@app/features/machinery/shared/models/data-list-offset-page-info.model';
import { LoadOptions } from 'devextreme/data';
import { DxGridQueryPrepareService } from '@core/api/services/dx-grid-query-prepare.service';
import { NotesDataService } from '@core/api/services/notes/notes-data.service';
import { NotesModel, NoteDetailModel, CreateNoteModel } from '@core/api/models/notes';

@Injectable({
  providedIn: 'root',
})
export class DailyPlansService
  extends BaseApiService<ApiServiceName.Manufactory, ManufactoryControllerEnum.DailyPlans>
  implements NotesDataService
{
  protected override readonly _apiService: ApiService;
  private readonly _httpClient = inject(HttpClient);
  private readonly _dxQueryParamsService = inject(DxGridQueryPrepareService);

  segmentsTotalRows: { [key: string]: number } = {};

  constructor() {
    const _apiService = inject(ApiService);

    super(_apiService, ApiServiceName.Manufactory, ManufactoryControllerEnum.DailyPlans);

    this._apiService = _apiService;
  }

  public getDailyPlans(
    options: LoadOptions,
    additionalParams?: {
      [key: string]: string;
    },
  ): Observable<DataListModel<DailyPlanListItemModel>> {
    let params = this._dxQueryParamsService.convertLoadOptionsToParams(options);
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        if ((Array.isArray(value) && value.length > 0) || (!Array.isArray(value) && value)) {
          if (key === 'DataGroups' && Array.isArray(value)) {
            value.forEach(v => {
              params = params.append(key, v);
            });
          } else {
            params = params.append(key, value);
          }
        }
      });
    }
    return this._httpClient
      .get<{ segmentsTotalRows: any; tabData: DataListOffsetPageInfoModel<DailyPlanListItemModel> }>(this.baseUrl, { params: params })
      .pipe(
        map(data => {
          const d: DataListModel<DailyPlanListItemModel> = data.tabData as unknown as DataListModel<DailyPlanListItemModel>;
          d.data.forEach(a => {
            a.bid = a.id;
            a.id = a.operationId;
            a.machineError =
              !a.machine && !!a.plannedStartDate && new Date(a.plannedStartDate).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0);
            a.notesCount = a.notes?.length ?? 0;
          });
          if (data && typeof data.tabData.offsetPageInfo !== 'undefined') {
            d.totalRows = data.tabData.offsetPageInfo.totalCount;
            d.limit = data.tabData.offsetPageInfo.limit;
            d.offset = data.tabData.offsetPageInfo.offset;
          }
          return d;
        }),
      );
  }

  public list(params: HttpParams): Observable<DataListModel<DailyPlanListItemModel>> {
    return this._httpClient.get<DataListOffsetPageInfoModel<DailyPlanListItemModel>>(this.baseUrl, { params: params }).pipe(
      map(data => {
        const d: DataListModel<DailyPlanListItemModel> = data as unknown as DataListModel<DailyPlanListItemModel>;
        if (data && typeof data.offsetPageInfo !== 'undefined') {
          d.totalRows = data.offsetPageInfo.totalCount;
          d.limit = data.offsetPageInfo.limit;
          d.offset = data.offsetPageInfo.offset;
        }
        return d;
      }),
    );
  }

  public addMachineToDailyPlan(operationId: string, machineNumber: string): Observable<unknown> {
    return this._httpClient.put<string>(this.baseUrl + '/' + operationId + '/machines', { number: machineNumber });
  }

  public taskAssignment(operationId: string) {
    return this._httpClient.put<string>(this.baseUrl + '/task-assignment', { operationId });
  }

  public detail(id: string): Observable<DailyPlanDetailModel> {
    return this._httpClient.get<DailyPlanDetailModel>(`${this.baseUrl}/${id}`).pipe(map(entity => ({ ...entity, id })));
  }

  public createComplaint(operationId: string, data: DailyPlanCreateRequestForChange) {
    return this._httpClient.post<string>(this.baseUrl + '/' + operationId + '/request-for-change', data);
  }

  // Notes
  public getNotes(id: string, showHidden = false, stepCode?: number): Observable<NotesModel> {
    let params = new HttpParams().append('showHidden', showHidden);
    if (stepCode) {
      params = params.append('stepCode', stepCode);
    }
    return this._httpClient
      .get<NotesModel>(`${this.baseUrl}/${id}/notes`, { params })
      .pipe(map(data => ({ notes: data.notes, totalCount: data.notes.length })));
  }

  public getNoteHistory(id: string, index: number): Observable<NoteDetailModel[]> {
    return this._httpClient.get<NotesModel>(`${this.baseUrl}/${id}/notes/${index}`).pipe(map(data => data?.notes));
  }

  public createNote(id: string, model: CreateNoteModel, stepCode?: number): Observable<void> {
    return this._httpClient.post<void>(`${this.baseUrl}/${id}/notes`, { ...model, stepCode });
  }

  public updateNote(id: string, model: CreateNoteModel, index: number): Observable<void> {
    return this._httpClient.post<void>(`${this.baseUrl}/${id}/notes/${index}`, model);
  }

  public toggleNoteVisibility(id: string, isVisible: boolean, index: number): Observable<void> {
    return this._httpClient.put<void>(`${this.baseUrl}/${id}/notes/${index}/visibility`, isVisible);
  }

  public updateLocks(id: string, locks: number[]) {
    return this._httpClient.put<any>(`${this.baseUrl}/${id}/locks`, { locks });
  }
}
